import { useEffect } from "react";
import { getAuth } from "firebase/auth";
import { scheduleSharePath } from "./schedules/scheduleConstants";

// Where HomeGate sends a logged-out /home/* hit. The public-share carve-outs
// rewrite content links to their share views; everything else goes to the
// sign-in splash at "/" (a ?modal=profile link, or a return link: a shared
// item or a team-invite Team page link, goes to the auth modal and sign-in
// comes back to it). Extracted from App.jsx (same reasoning as
// legacyRedirects.tsx) so tests exercise the REAL rewrite logic instead of a
// hand-copied stub that keeps passing after App.jsx drifts.
export function loggedOutHomeRedirect(
  pathname: string,
  search = "",
  wasSignedIn = false,
): string {
  const params = new URLSearchParams(search);
  // Checked before the concept carve-out: a share invite's concept link
  // (?invite=1) asks for sign-in, so an editor gets the editable page.
  if (!wasSignedIn && isReturnLink(pathname, params)) {
    const next = encodeURIComponent(`${pathname}${search}`);
    const auth =
      TEAM_PATH.test(pathname) && params.get("signup") === "1"
        ? "signup"
        : "signin";
    return `/start_login_ds?auth=${auth}&next=${next}`;
  }
  const conceptMatch = pathname.match(
    /^\/home\/concepts2\/(?:editor\/)?([^/]+)\/?$/,
  );
  if (conceptMatch && conceptMatch[1] !== "new") {
    return `/home/concepts2/share/${conceptMatch[1]}`;
  }
  // Editor links to a schedule → its public share view (which shows a
  // sign-in prompt when the schedule isn't public). The share route itself
  // never reaches HomeGate — it is a more specific top-level route.
  const scheduleMatch = pathname.match(/^\/home\/schedules\/([^/]+)\/?$/);
  if (scheduleMatch && scheduleMatch[1] !== "share") {
    return scheduleSharePath(scheduleMatch[1]);
  }
  // A link from the page's brief /home/materials era → the canonical public
  // URL (a top-level route that never reaches HomeGate). The query rides
  // along so utm attribution survives, matching the logged-in redirect
  // (HomeMaterialsRedirect). Deliberately scoped to this branch: the share
  // carve-outs above have always dropped the query.
  const materialMatch = pathname.match(/^\/home\/materials\/([^/]+)\/?$/);
  if (materialMatch) {
    return `/materials/${materialMatch[1]}${search}`;
  }
  // Virtual Showrooms have a public twin under /showrooms (rendered by
  // PublicShowroomShell, which applies its own staging/launch gate).
  const showroomMatch = pathname.match(/^\/home\/showrooms(?:\/([^/]+))?\/?$/);
  if (showroomMatch) {
    return showroomMatch[1] ? `/showrooms/${showroomMatch[1]}` : "/showrooms";
  }
  // The SketchUp panel's "Finish your profile" link: open the auth modal
  // straight away (the splash CTA would overwrite redirectTo); signing in or
  // up there returns here (authRedirect / SignUp honour redirectTo). Only on a
  // signed-out visit: when someone signs out on the page (logout, account
  // deletion, an ended session) HomeGate passes wasSignedIn, and the page being
  // left is not a link to come back to.
  if (wasSignedIn) return "/";
  if (params.get("modal") === "profile") {
    sessionStorage?.setItem("redirectTo", `${pathname}${search}`);
    return "/start_login_ds";
  }
  return "/";
}

// Return links: links sign-in comes back to. A return link travels in the
// sign-in page's URL (?next=, see useLinkSignIn), so it lasts exactly as long
// as that page.
// - A team-invite Team page link carrying ?team=. It opens on sign-in, or on
//   sign-up when the invite email found no account for the address (signup=1);
//   each form links to the other.
// - A shared collection or project, the share invite's links.
// - A concept only from a share invite (?invite=1): a pasted concept link
//   opens the public share page instead (the carve-out above).
const TEAM_PATH = /^\/home\/team(\/|$)/;
const ITEM_PATH = /^\/home\/(collections|projects)\/[^/]+\/?$/;
const CONCEPT_PATH = /^\/home\/concepts2\/[^/]+\/?$/;

function isReturnLink(pathname: string, params: URLSearchParams): boolean {
  if (TEAM_PATH.test(pathname)) return !!params.get("team");
  if (CONCEPT_PATH.test(pathname)) return params.get("invite") === "1";
  return ITEM_PATH.test(pathname);
}

// The return link a sign-in page URL carries (?next=), or null. Anyone can set
// ?next=, so only a same-origin return link counts: it can't send a sign-in
// anywhere else.
export function linkFromSignInUrl(search: string): string | null {
  const next = new URLSearchParams(search).get("next");
  if (!next) return null;
  let url: URL;
  try {
    url = new URL(next, window.location.origin);
  } catch {
    return null;
  }
  if (url.origin !== window.location.origin) return null;
  if (!isReturnLink(url.pathname, url.searchParams)) return null;
  return `${url.pathname}${url.search}`;
}

// The pending return link the sign-in page stamped, if any (read only:
// HomeLayout clears it once that page is reached). Other stamps return null.
export function pendingLinkRedirect(): string | null {
  const redirectTo = sessionStorage?.getItem("redirectTo");
  if (!redirectTo) return null;
  const path = redirectTo.split("?")[0];
  return [TEAM_PATH, ITEM_PATH, CONCEPT_PATH].some((p) => p.test(path))
    ? redirectTo
    : null;
}

// The sign-in was abandoned: forget a pending return link, so a later sign-in
// in this tab doesn't land there. Other stamps (pricing, SketchUp checkout)
// are left alone.
export function clearAbandonedLinkRedirect() {
  if (pendingLinkRedirect()) sessionStorage.removeItem("redirectTo");
}

// The sign-in page a return link opens (StartLoginDs) owns that link. It
// keeps it in its URL (?next=) and hands it to the sign-in forms through
// redirectTo, stamped on every load (a reload too) and on a return from the
// back/forward cache. Leaving the page signed out forgets the stamp, whichever
// way: closing the modal, Back, a typed URL (pagehide), or an in-app link
// (unmount). Signed in, it is kept: that sign-in is following it.
// getAuth().currentUser, not the context, which updates a beat later; OAuth
// signs in by popup, so a sign-in never leaves the page.
export function useLinkSignIn() {
  useEffect(() => {
    const page = window.location.pathname;
    const stamp = () => {
      const link = linkFromSignInUrl(window.location.search);
      if (link) sessionStorage.setItem("redirectTo", link);
    };
    const restored = (event: PageTransitionEvent) => {
      if (event.persisted) stamp();
    };
    const forget = () => {
      if (!getAuth().currentUser) clearAbandonedLinkRedirect();
    };
    stamp();
    window.addEventListener("pageshow", restored);
    window.addEventListener("pagehide", forget);
    return () => {
      window.removeEventListener("pageshow", restored);
      window.removeEventListener("pagehide", forget);
      // StrictMode remounts in place; only a navigation elsewhere counts.
      setTimeout(() => {
        if (window.location.pathname !== page) forget();
      }, 0);
    };
  }, []);
}
